import AuthGuard from "@/components/AuthGuard/index.ts";
import MultiProvider from "@/components/MultiProvider/index.ts";
import { FullPageSpinner } from "@/components/Spinner";
import ThemeProvider from "@/components/ThemeProvider/index.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import {
    type QueryOptionsFactory,
    QueryOptionsFactoryProvider,
    createQueryOptionsFactory,
} from "@/queries";
import { extendedReplaceEqualDeep } from "@/utils/api.ts";
import { Auth0Provider } from "@auth0/auth0-react";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { CssBaseline } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { SnackbarProvider } from "notistack";
import { type ReactNode, StrictMode } from "react";
import { ConfirmProvider } from "react-confirm-hook";
import { createRoot } from "react-dom/client";
import { routeTree } from "./routeTree.gen.ts";
import "@js-joda/timezone/dist/js-joda-timezone-10-year-range.esm.js";

const container = document.getElementById("root");

if (!container) {
    throw new Error("Root element missing");
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            structuralSharing: extendedReplaceEqualDeep,
            retry: false,
        },
    },
});
const root = createRoot(container);

const router = createRouter({
    routeTree,
    defaultPendingComponent: FullPageSpinner,
    context: {
        queryClient,
        // Injected through React
        qof: undefined as unknown as QueryOptionsFactory,
    },
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
});

declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}

const App = (): ReactNode => {
    const authFetch = useAuthenticatedFetch();
    const queryOptionsFactory = createQueryOptionsFactory(authFetch);

    return (
        <QueryOptionsFactoryProvider factory={queryOptionsFactory}>
            <RouterProvider
                router={router}
                context={{
                    qof: queryOptionsFactory,
                }}
            />
        </QueryOptionsFactoryProvider>
    );
};

root.render(
    <StrictMode>
        <MultiProvider
            providerCreators={[
                (children) => (
                    <ThemeProvider>
                        <CssBaseline />
                        {children}
                    </ThemeProvider>
                ),
                (children) => (
                    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
                ),
                (children) => (
                    <Auth0Provider
                        domain={import.meta.env.VITE_APP_AUTH0_DOMAIN}
                        clientId={import.meta.env.VITE_APP_AUTH0_CLIENT_ID}
                        authorizationParams={{
                            redirect_uri: window.location.origin,
                            audience: import.meta.env.VITE_APP_AUTH0_AUDIENCE,
                        }}
                        onRedirectCallback={(appState) => {
                            // Required so that the app can render the RouterProvider before
                            // redirection happens.
                            window.requestAnimationFrame(() => {
                                void router.navigate({ to: appState?.returnTo ?? "/" });
                            });
                        }}
                        useRefreshTokens
                        cacheLocation="localstorage"
                    >
                        {children}
                    </Auth0Provider>
                ),
                (children) => <SnackbarProvider>{children}</SnackbarProvider>,
                (children) => <ConfirmProvider>{children}</ConfirmProvider>,
                (children) => (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        {children}
                    </LocalizationProvider>
                ),
                (children) => <AuthGuard>{children}</AuthGuard>,
            ]}
        >
            <App />
        </MultiProvider>
    </StrictMode>,
);
